/**
 * The **session log** — the canonical, lossless model of one conversation.
 *
 * This is the single contract that the memory-graph data path is built around
 * (see `plans/memory-unification.md`). Both the live turn stream and a session
 * restore are mapped into an ordered list of {@link SessionLogEvent}s, and the
 * Memory Graph is a *pure projection* of that list (see `sessionLogProjections.ts`).
 *
 * Design intent:
 *  - **One representation, many views.** The conversation is recorded once, here,
 *    in full; surfaces only *filter/categorize* it — they never re-summarize or
 *    drop information of their own.
 *  - **Live and restored produce the same shape.** Phase 1 synthesizes the log
 *    from today's sources (rich stream / lossy transcript) via adapters; Phase 2
 *    has the server emit + persist this shape directly. Either way the projection
 *    layer is unchanged.
 *  - Distinct from the **knowledge base** (`/agent-memory`, the full stored
 *    episodic/semantic store) that feeds the "All" list — that is a separate
 *    source by design; the {@link SessionLogEvent} of type `memory-recall` is the
 *    bridge (it records *which* stored records were pulled into this conversation).
 */

import type { AgentCitation, StreamedMemoryRecord } from './agentStream.ts';

/** The memory module a record belongs to. The agent's stored memory is split into
 *  `session` (this conversation's turns), `episodic` (past conversations' turns)
 *  and `semantic` (durable facts). Carried explicitly on every {@link LogMemRecord}
 *  so views never have to *guess* a tier from a module-id string. */
export type RecordTier = 'session' | 'episodic' | 'semantic';

/** One memory record as it appears in the log — a {@link StreamedMemoryRecord}
 *  with its tier made explicit. `kind` mirrors the runtime: `turn` (a stored
 *  conversation turn) or `fact` (a distilled semantic fact). */
export interface LogMemRecord {
  readonly id: string;
  readonly tier: RecordTier;
  readonly kind: StreamedMemoryRecord['kind'];
  readonly text: string;
  readonly ts: string;
}

/** Whether a tool call settled successfully. */
export type ToolCallStatus = 'ok' | 'error';

/**
 * One event in a session's lossless log. `id` is a stable per-event id (used for
 * React keys, dedup, and idempotent graph reconciliation); `ts` is the event's
 * ISO timestamp. Events are append-only and ordered.
 */
export type SessionLogEvent =
  | { readonly type: 'user-message'; readonly id: string; readonly ts: string; readonly text: string }
  | {
      readonly type: 'assistant-message';
      readonly id: string;
      readonly ts: string;
      readonly text: string;
      readonly citations?: readonly AgentCitation[];
    }
  | {
      readonly type: 'tool-call';
      readonly id: string;
      readonly ts: string;
      readonly name: string;
      readonly args: Record<string, unknown>;
      readonly output: string;
      readonly status: ToolCallStatus;
    }
  | {
      // Records the agent *created* this turn (already carried out-of-band on the
      // turn stream as `memory-write`).
      readonly type: 'memory-write';
      readonly id: string;
      readonly ts: string;
      readonly records: readonly LogMemRecord[];
    }
  | {
      // Records the agent *recalled* into context this turn. Out-of-band (like
      // `memory-write`), so it may carry ids the LLM-facing `memory_recall` tool
      // output deliberately strips. This is the bridge to the knowledge base.
      readonly type: 'memory-recall';
      readonly id: string;
      readonly ts: string;
      readonly records: readonly LogMemRecord[];
    };

/** Narrow a log event by `type` (handy in projections/tests). */
export type SessionLogEventOf<T extends SessionLogEvent['type']> = Extract<
  SessionLogEvent,
  { type: T }
>;

/**
 * Resolve a runtime module id (e.g. `session`, `episodic-v2`, `semantic`) to a
 * {@link RecordTier}. The runtime names its memory modules after their tier, but
 * the exact string can vary; mapping to a fixed set of tiers here is what lets the
 * graph and list **group by tier** instead of by raw module id — the fix for the
 * "two Episodic sections" bug (two distinct ids that both meant episodic). Unknown
 * ids fall back to `episodic` (the long-term conversational tier).
 */
export function normalizeTier(moduleId: string): RecordTier {
  const id = moduleId.toLowerCase();
  if (id.includes('session') || id.includes('working') || id.includes('conversation')) {
    return 'session';
  }
  if (id.includes('semant') || id.includes('fact')) {
    return 'semantic';
  }
  return 'episodic';
}
